import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

interface TextToSpeechProps {
  rate?: number;
  pitch?: number;
  volume?: number;
  lang?: string;
  defaultEnabled?: boolean;
  showToggle?: boolean;
  toggleClassName?: string;
  onEnabledChange?: (enabled: boolean) => void;
}

/**
 * Text-to-Speech component with global hover-to-speak functionality
 * 
 * Features:
 * - Toggle button to enable/disable text-to-speech
 * - Hover over any text element to hear it spoken
 * - Persists enabled state in localStorage
 * - Synchronizes state across multiple component instances
 * - Uses female voice by default
 */
export const TextToSpeech: React.FC<TextToSpeechProps> = ({
  rate = 1.0,
  pitch = 1.0,
  volume = 0.8,
  lang = 'en-US',
  defaultEnabled = false,
  showToggle = true,
  toggleClassName = '',
  onEnabledChange,
}) => {
  const [enabled, setEnabled] = useState(() => {
    const saved = localStorage.getItem('textToSpeechEnabled');
    return saved !== null ? saved === 'true' : defaultEnabled;
  });

  const enabledRef = useRef(enabled);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  // Sync enabledRef with enabled state
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Save to localStorage and dispatch custom event when enabled changes
  useEffect(() => {
    localStorage.setItem('textToSpeechEnabled', enabled.toString());
    
    // Dispatch custom event for synchronization across components
    window.dispatchEvent(
      new CustomEvent('textToSpeechToggle', { detail: { enabled } })
    );

    if (onEnabledChange) {
      onEnabledChange(enabled);
    }
  }, [enabled, onEnabledChange]);

  // Listen for external toggle events
  useEffect(() => {
    const handleToggle = (e: CustomEvent) => {
      const { enabled: newEnabled } = e.detail;
      if (newEnabled !== enabledRef.current) {
        setEnabled(newEnabled);
      }
    };

    window.addEventListener('textToSpeechToggle', handleToggle as EventListener);
    return () => {
      window.removeEventListener('textToSpeechToggle', handleToggle as EventListener);
    };
  }, []);

  // Stop any ongoing speech
  const stop = useCallback(() => {
    if (utteranceRef.current) {
      window.speechSynthesis.cancel();
      utteranceRef.current = null;
    }
  }, []);

  // Get female voice for the specified language
  const getFemaleVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    
    if (voices.length === 0) {
      return null;
    }
    
    // Common female voice names across different platforms
    const femaleKeywords = [
      'female', 'samantha', 'karen', 'victoria', 'zira', 'susan', 'kate', 
      'shelley', 'veena', 'lekha', 'priya', 'maria', 'linda', 'nancy',
      'heather', 'sarah', 'emma', 'amy', 'lisa', 'michelle', 'jessica',
      'google hindi', 'hindi female', 'hi-in', 'hi_in', 'marathi', 'mr-in', 'mr_in',
      'tamil', 'ta-in', 'ta_in', 'telugu', 'te-in', 'te_in', 'arabic', 'ar-sa', 'ar_sa',
      'urdu', 'bengali', 'gujarati', 'kannada', 'malayalam', 'odia', 'punjabi', 'assamese'
    ];
    
    // Common male voice names to exclude
    const maleKeywords = [
      'male', 'alex', 'daniel', 'fred', 'ravi', 'tom', 'david', 'john',
      'mark', 'paul', 'richard', 'robert', 'thomas', 'william'
    ];
    
    // Language code mappings
    const langPrefix = lang.split('-')[0].toLowerCase();
    const langCodeMap: Record<string, string[]> = {
      'hi': ['hi', 'hindi', 'hi-in', 'hi_in'],
      'mr': ['mr', 'marathi', 'mr-in'],
      'ta': ['ta', 'tamil', 'ta-in'],
      'te': ['te', 'telugu', 'te-in'],
      'ur': ['ur', 'urdu', 'ur-pk'],
      'bn': ['bn', 'bengali', 'bn-in'],
      'gu': ['gu', 'gujarati', 'gu-in'],
      'kn': ['kn', 'kannada', 'kn-in'],
      'ml': ['ml', 'malayalam', 'ml-in'],
      'pa': ['pa', 'punjabi', 'pa-in'],
      'or': ['or', 'odia', 'or-in'],
      'as': ['as', 'assamese', 'as-in'],
      'en': ['en', 'english', 'en-us', 'en-gb'],
      'fr': ['fr', 'french', 'fr-fr'],
      'es': ['es', 'spanish', 'es-es'],
      'de': ['de', 'german', 'de-de'],
      'it': ['it', 'italian', 'it-it'],
      'pt': ['pt', 'portuguese', 'pt-pt'],
      'ru': ['ru', 'russian', 'ru-ru'],
      'ja': ['ja', 'japanese', 'ja-jp'],
      'ko': ['ko', 'korean', 'ko-kr'],
      'zh': ['zh', 'chinese', 'zh-cn'],
      'ar': ['ar', 'arabic', 'ar-sa'],
    };
    
    // Get language codes to search for
    const langCodes = langCodeMap[langPrefix] || [langPrefix];
    
    // First, try to find a female voice matching the language
    let femaleVoice = voices.find(voice => {
      const voiceName = voice.name.toLowerCase();
      const voiceLang = voice.lang.toLowerCase();
      const isFemale = femaleKeywords.some(keyword => voiceName.includes(keyword));
      const isMale = maleKeywords.some(keyword => voiceName.includes(keyword));
      
      // Check if language matches any of the language codes
      const langMatches = langCodes.some(code => 
        voiceLang.includes(code.toLowerCase()) || 
        voiceName.includes(code.toLowerCase())
      );
      
      return isFemale && !isMale && langMatches;
    });
    
    // If no female voice found, try to find any voice with matching language
    if (!femaleVoice) {
      femaleVoice = voices.find(voice => {
        const voiceLang = voice.lang.toLowerCase();
        const voiceName = voice.name.toLowerCase();
        return langCodes.some(code => 
          voiceLang.includes(code.toLowerCase()) || 
          voiceName.includes(code.toLowerCase())
        );
      });
    }
    
    // If still no voice found, try to find any female voice
    if (!femaleVoice) {
      const anyFemaleVoice = voices.find(voice => {
        const voiceName = voice.name.toLowerCase();
        const isFemale = femaleKeywords.some(keyword => voiceName.includes(keyword));
        const isMale = maleKeywords.some(keyword => voiceName.includes(keyword));
        return isFemale && !isMale;
      });
      return anyFemaleVoice || null;
    }
    
    return femaleVoice || null;
  }, [lang]);

  // Speak text
  const speak = useCallback(
    (text: string) => {
      if (!enabledRef.current || !text || !text.trim()) return;

      stop(); // Stop any ongoing speech

      const utterance = new SpeechSynthesisUtterance(text.trim());
      utterance.rate = rate;
      utterance.pitch = pitch;
      utterance.volume = volume;
      
      // Language code mappings for proper language detection
      const langCodeMap: Record<string, string> = {
        'hi': 'hi-IN',
        'mr': 'mr-IN',
        'ta': 'ta-IN',
        'te': 'te-IN',
        'ur': 'ur-PK',
        'bn': 'bn-IN',
        'gu': 'gu-IN',
        'kn': 'kn-IN',
        'ml': 'ml-IN',
        'pa': 'pa-IN',
        'or': 'or-IN',
        'as': 'as-IN',
        'en': 'en-US',
        'fr': 'fr-FR',
        'es': 'es-ES',
        'de': 'de-DE',
        'it': 'it-IT',
        'pt': 'pt-PT',
        'ru': 'ru-RU',
        'ja': 'ja-JP',
        'ko': 'ko-KR',
        'zh': 'zh-CN',
        'ar': 'ar-SA',
      };
      
      // Set language - use proper language code
      const langPrefix = lang.split('-')[0].toLowerCase();
      let langCode = langCodeMap[langPrefix] || lang;
      
      // Detect language from localStorage (Google Translate now uses localStorage)
      try {
        const storedLangCode = localStorage.getItem('google-translate-language');
        if (storedLangCode && langCodeMap[storedLangCode]) {
          langCode = langCodeMap[storedLangCode];
        } else if (storedLangCode && storedLangCode.length === 2) {
          // Map common language codes
          langCode = langCodeMap[storedLangCode] || `${storedLangCode}-${storedLangCode.toUpperCase()}`;
        }
      } catch (error) {
        // Fallback to cookie if localStorage fails
        const cookies = document.cookie.split(';');
        const googtransCookie = cookies.find(c => c.trim().startsWith('googtrans='));
        if (googtransCookie) {
          const cookieValue = googtransCookie.split('=')[1];
          if (cookieValue && cookieValue !== '/en/en') {
            const decodedValue = decodeURIComponent(cookieValue).trim();
            const langMatch = decodedValue.match(/\/en\/([^\/]+)/);
            if (langMatch) {
              const translatedLang = langMatch[1].trim();
              // Map translated language to proper language code
              if (langCodeMap[translatedLang]) {
                langCode = langCodeMap[translatedLang];
              } else if (translatedLang.length === 2) {
                // Use the translated language code
                langCode = `${translatedLang}-${translatedLang.toUpperCase()}`;
              }
            }
          }
        }
      }
      
      utterance.lang = langCode;

      // Set female voice
      const femaleVoice = getFemaleVoice();
      if (femaleVoice) {
        utterance.voice = femaleVoice;
        // Use voice's language for better compatibility
        utterance.lang = femaleVoice.lang;
      }

      utteranceRef.current = utterance;

      utterance.onend = () => {
        utteranceRef.current = null;
      };

      utterance.onerror = (error) => {
        // Only log errors that aren't expected (not-allowed, interrupted, and canceled are common and expected)
        const errorEvent = error as SpeechSynthesisErrorEvent;
        if (errorEvent.error !== 'not-allowed' && errorEvent.error !== 'interrupted' && errorEvent.error !== 'canceled') {
          // Only log unexpected errors, not expected interruptions
          console.warn('Speech synthesis error:', errorEvent.error);
        }
        utteranceRef.current = null;
      };

      window.speechSynthesis.speak(utterance);
    },
    [rate, pitch, volume, lang, stop, getFemaleVoice]
  );

  // Load voices when component mounts
  useEffect(() => {
    // Voices might not be loaded immediately, so we need to wait for them
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        // Voices are loaded
        return;
      }
    };

    // Try to load voices immediately
    loadVoices();

    // Some browsers load voices asynchronously, so listen for the voiceschanged event
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);

    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
    };
  }, []);

  // Global hover-to-speak functionality
  useEffect(() => {
    if (!enabled) {
      stop();
      return;
    }

    // Get text content from an element, excluding script and style tags
    const getTextContent = (element: Element | null): string | null => {
      if (!element) return null;

      const tagName = element.tagName?.toLowerCase();
      
      // Skip script, style, and other non-text elements
      if (
        tagName === 'script' ||
        tagName === 'style' ||
        tagName === 'noscript' ||
        tagName === 'svg' ||
        tagName === 'path' ||
        tagName === 'input' ||
        tagName === 'select' ||
        tagName === 'textarea'
      ) {
        return null;
      }

      // Special handling for headings - prioritize them
      const isHeading = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName);
      
      // For buttons and links, only extract text if they're headings or have meaningful text
      if ((tagName === 'button' || tagName === 'a') && !isHeading) {
        // Only skip if it's clearly a navigation/interactive element without text content
        const text = element.textContent?.trim();
        if (!text || text.length === 0) {
          return null;
        }
      }

      // Extract text content - this works for headings and other elements
      const text = element.textContent?.trim();
      
      if (text && text.length > 0) {
        // For headings, allow longer text (up to 1000 chars)
        // For other elements, limit to 500 chars
        const maxLength = isHeading ? 1000 : 500;
        if (text.length <= maxLength) {
          return text;
        }
        // If text is too long, truncate it
        return text.substring(0, maxLength) + '...';
      }

      return null;
    };

    // Find the closest text-containing element, prioritizing headings
    const findTextElement = (target: EventTarget | null): Element | null => {
      if (!(target instanceof Element)) return null;

      let current: Element | null = target;
      let headingElement: Element | null = null;

      // First pass: look for headings up to 5 levels (prioritize headings)
      for (let i = 0; i < 5 && current; i++) {
        const tagName = current.tagName?.toLowerCase();
        if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
          const text = getTextContent(current);
          if (text) {
            headingElement = current;
            break; // Found a heading, prioritize it
          }
        }
        current = current.parentElement;
      }

      // If we found a heading, return it
      if (headingElement) {
        return headingElement;
      }

      // Second pass: look for any text element (reset to original target)
      current = target;
      for (let i = 0; i < 5 && current; i++) {
        const text = getTextContent(current);
        if (text) {
          return current;
        }
        current = current.parentElement;
      }

      return null;
    };

    const handleMouseEnter = (e: MouseEvent) => {
      if (!enabledRef.current) return; // Use ref to avoid stale closure

      const textElement = findTextElement(e.target);
      if (textElement) {
        const text = getTextContent(textElement);
        if (text) {
          speak(text);
        }
      }
    };

    const handleMouseLeave = () => {
      if (enabledRef.current) {
        stop();
      }
    };

    // Add listeners to common text-containing elements
    const addListeners = () => {
      const selectors = [
        'p',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'span',
        'div',
        'li',
        'td',
        'th',
        'label',
        'article',
        'section',
        'main',
        'aside',
        'header',
        'footer',
        'nav',
      ];

      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          el.addEventListener('mouseenter', handleMouseEnter as EventListener);
          el.addEventListener('mouseleave', handleMouseLeave);
        });
      });
    };

    const removeListeners = () => {
      const selectors = [
        'p',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'span',
        'div',
        'li',
        'td',
        'th',
        'label',
        'article',
        'section',
        'main',
        'aside',
        'header',
        'footer',
        'nav',
      ];

      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
          el.removeEventListener('mouseenter', handleMouseEnter as EventListener);
          el.removeEventListener('mouseleave', handleMouseLeave);
        });
      });
    };

    // Initial listener setup
    addListeners();

    // Watch for dynamically added content
    observerRef.current = new MutationObserver(() => {
      if (enabledRef.current) {
        removeListeners();
        addListeners();
      }
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      stop();
      removeListeners();
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [enabled, speak, stop]);

  const handleToggle = () => {
    setEnabled((prev) => !prev);
  };

  if (showToggle) {
    return (
      <button
        onClick={handleToggle}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${toggleClassName}`}
        title={enabled ? 'Disable text-to-speech' : 'Enable text-to-speech'}
        aria-label={enabled ? 'Disable text-to-speech' : 'Enable text-to-speech'}
      >
        {enabled ? (
          <Volume2 className="w-4 h-4 text-blue-500" />
        ) : (
          <VolumeX className="w-4 h-4 text-gray-400" />
        )}
        <span className="text-sm font-medium text-gray-700 hidden md:inline">
          {enabled ? '' : ''}
        </span>
      </button>
    );
  }

  return null;
};

/**
 * Toggle button component for text-to-speech
 * Uses default settings and shows toggle button
 */
export const TextToSpeechToggle: React.FC<{ className?: string }> = ({
  className = '',
}) => {
  return (
    <TextToSpeech
      showToggle={true}
      toggleClassName={className}
      defaultEnabled={false}
    />
  );
};

/**
 * Global text-to-speech component (no toggle button)
 * Use this when you want the functionality without the UI
 */
export const GlobalTextToSpeech: React.FC<{
  rate?: number;
  pitch?: number;
  volume?: number;
  lang?: string;
}> = ({ rate = 1.0, pitch = 1.0, volume = 0.8, lang = 'en-US' }) => {
  return (
    <TextToSpeech
      showToggle={false}
      rate={rate}
      pitch={pitch}
      volume={volume}
      lang={lang}
    />
  );
};

